import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';

import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';

import CancelIcon from '@mui/icons-material/Cancel';
import { Countries } from '../../../../constants';
import { isMobileScreen } from '../../../../helper';
import { getDiagnosticState } from '../../../../store/DiagnosticSlice';
import { useAppSelector } from '../../../../hooks';

const buttonProps: ButtonProps = {
  style: { fontWeight: 600, padding: '6px 40px', marginLeft: '15px' },
  variant: 'contained',
};

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

// DATA
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === timezone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

export interface IShareParentState {
  parentName: string;
  parentPhoneNumber: string;
}

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (values: IShareParentState) => void;
}

const initialState: IShareParentState = {
  parentName: '',
  parentPhoneNumber: '',
};

const defaultErrorData = {
  visible: false,
  message: '',
};

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const isMobile = isMobileScreen();

  // State
  const [state, setState] = useState(initialState);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    parentName: { ...defaultErrorData },
    parentPhoneNumber: { ...defaultErrorData },
  });

  // Store
  const diagnosticState = useAppSelector(getDiagnosticState);

  useEffect(() => {
    if (state.parentName) {
      let _nameError = { ...defaultErrorData };

      if (state.parentName.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        parentName: _nameError,
      }));
    }
  }, [state.parentName]);

  useEffect(() => {
    if (state.parentPhoneNumber) {
      let _nameError = { ...defaultErrorData };

      if (state.parentPhoneNumber.length < 9) {
        _nameError = {
          visible: true,
          message: 'Phone Number should have at least 9 numbers',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        parentPhoneNumber: _nameError,
      }));
    }
  }, [state.parentPhoneNumber]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleShareButton = () => {
    onConfirm(state);
    setIsSubmitted(true);
    setTimeout(() => {
      onClose();
      setIsSubmitted(false);
    }, 5000);
  };

  const submitButtonDisabled =
    state.parentName.trim().length < 3 ||
    state.parentPhoneNumber.trim().length < 9;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          minWidth: { xs: 'unset', sm: '600px', md: '700px', lg: '800px' },
          maxWidth: { xs: 'unset', sm: '600px', md: '700px', lg: '800px' },
        },
      }}
    >
      {isSubmitted ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(180deg, #01B8AF 0%, #007B76 100%)',
            padding: '50px 0px',
          }}
        >
          {!isMobile && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 15,
                top: 15,
                color: 'white',
                padding: 0,
              }}
            >
              <CancelIcon style={{ width: '30px', height: '30px' }} />
            </IconButton>
          )}
          <Typography
            color="white"
            variant="h6"
            fontWeight="600"
            marginBottom="10px"
          >
            Shared Successfully
          </Typography>
          <img
            src={`${process.env.PUBLIC_URL}/images/study-girl.png`}
            alt="study-girl"
            style={{ width: '85%', maxWidth: '300px' }}
          />
        </div>
      ) : (
        <Stack direction="row">
          <div style={{ width: isMobile ? '100%' : '60%', padding: '10px' }}>
            <DialogTitle fontWeight="600" align="center">
              Hi {diagnosticState.diagnostic?.fullName.split(' ')[0] || ''},
              Let’s share your achievement
              {!isMobile && (
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    position: 'absolute',
                    right: 15,
                    top: 15,
                    color: 'white',
                    padding: 0,
                  }}
                >
                  <CancelIcon style={{ width: '30px', height: '30px' }} />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                fontSize="12px"
                fontWeight="600"
                align="center"
                marginBottom="30px"
              >
                Kindly fill in the details below so we can share your results
              </DialogContentText>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <FormControl fullWidth>
                    <Typography {...label}>Parent’s name *</Typography>
                    <TextField
                      size="small"
                      name="parentName"
                      type="text"
                      placeholder="Type your parent’s name here"
                      onChange={handleInputChange}
                      error={errors.parentName.visible}
                      helperText={errors.parentName.message || ' '}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Typography {...label}>Parent’s phone number *</Typography>
                  <PhoneInput
                    international
                    withCountryCallingCode
                    defaultCountry={defaultCountryCode}
                    placeholder="Enter phone number"
                    value={state.parentPhoneNumber}
                    onChange={(value) => {
                      if (value) {
                        setState((prevState) => ({
                          ...prevState,
                          parentPhoneNumber: value,
                        }));
                      }
                    }}
                    className="phone-number-input-custom-styles"
                  />
                  <FormHelperText
                    style={{ marginLeft: '14px', color: '#FF2B2D' }}
                  >
                    {errors.parentPhoneNumber.message || ' '}
                  </FormHelperText>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: '24px' }}>
              <Button {...buttonProps} color="inherit" onClick={onClose}>
                Cancel
              </Button>
              <Button
                {...buttonProps}
                color="primary"
                disabled={submitButtonDisabled}
                onClick={handleShareButton}
              >
                Share
              </Button>
            </DialogActions>
          </div>
          {!isMobile && (
            <div
              style={{
                width: '40%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'linear-gradient(180deg, #01B8AF 0%, #007B76 100%)',
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/study-girl.png`}
                alt="study-girl"
                style={{ width: '85%', maxWidth: '90vw' }}
              />
            </div>
          )}
        </Stack>
      )}
    </Dialog>
  );
};

export default ShareModal;
